import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DateInDateModal } from './DateInDateModal'
import { PlanType, CheckInDateType, searchData } from './type'
import { usePlanStore } from '../../../store/planStore'
import { IconInbox } from './Icon'
import { useSearchStore } from '../../../store/jpeTopPageStore'
import { useLpAvailabilityPageStore } from '../../../store/lpAvailabilityPageStore'
import { formatConvertedPrice, formatJPYPrice } from '../../jpe-top-page/utils'
import { WHATSAPP_CONSULTATION_URL } from '../../../constants/contactUrls'
import { LastMinuteInquiryModal } from './LastMinuteInquiryModal'
import { useLastMinuteInquiryStore } from '../../../store/lastMinuteInquiryStore'
import { AvailabilityStatusIcon } from '../../availability-status/AvailabilityStatusIcons'
import { AvailabilityStatusLabel, availabilityStatusAriaLabel } from '../../availability-status/AvailabilityStatusLabel'
import { statusToDisplayKind } from '../../availability-status/statusFromCheckInDate'

// [#2925] 「問い合わせ」の日の判定に使える生年月日が URL にあるか。サーバー（CheckInDate#summary_birthdays）と同じく
// 年・月・日が揃ったものだけを数える（空や壊れた値だと、サーバーは園の受入枠を判定せず「問い合わせ」のまま返すため）
const hasValidBirthdays = () =>
  (new URLSearchParams(window.location.search).get('birthdays') || '')
    .split(',')
    .some((s) => /^\d{4}-\d{1,2}-\d{1,2}$/.test(s.trim()))

export const CheckInDate = (props: { plan: PlanType; checkInDate: CheckInDateType }) => {
  const { plan, checkInDate } = props
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showLastMinuteInquiryModal, setShowLastMinuteInquiryModal] = useState<boolean>(false)
  const ref = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const initializeLastMinuteInquiry = useLastMinuteInquiryStore((state) => state.initializeLastMinuteInquiry)
  const { selectedPlan, setSelectedPlan, selectedCheckInDate, setSelectedCheckInDate, setValidationErrors, setOpenModalFilter } =
    usePlanStore()
  const { selectedCurrency } = useSearchStore()
  const { isJpe } = useLpAvailabilityPageStore()
  const displayCurrency = isJpe && selectedCurrency === 'JPY' ? 'USD' : selectedCurrency
  const serviceTypes = JSON.parse(
    (document.querySelector('[data-service-types]') as HTMLElement)?.dataset?.serviceTypes || '[]',
  ) as string[]
  const displayKind = statusToDisplayKind(checkInDate.status)
  const isSelectable = checkInDate.status === 'OK' || checkInDate.status === '-'

  useEffect(() => {
    let dateView = searchData.start_date
    if (!dateView) {
      const urlParams = new URLSearchParams(window.location.search)
      const monthStrings = urlParams.get('select_month') || '' // "2025/08,2025/06,2025/10"
      const months = monthStrings.split(',')
      if (months.length) {
        const selected = plan.checkInDates.find((date) => {
          const [dateYear, dateMonth] = date.date.split('-')
          const checkInMonthString = `${dateYear}/${dateMonth}`
          return months.includes(checkInMonthString)
        })

        if (checkInDate === selected) dateView = checkInDate.date
      }
    }

    if (dateView === checkInDate.date && ref.current) {
      const parent = ref.current.parentElement
      if (parent) parent.scrollLeft = ref.current.offsetLeft - parent.offsetLeft
    }
  }, [searchData])

  useEffect(() => {
    let dateView = searchData.start_date
    if (!dateView) {
      const firstAvailableDate = plan.checkInDates.find((date) => date.status === 'OK' || date.status === '-')
      if (firstAvailableDate) dateView = firstAvailableDate.date
    }

    if (dateView === checkInDate.date && ref.current) {
      const parent = ref.current.parentElement
      if (parent) parent.scrollLeft = ref.current.offsetLeft - parent.offsetLeft - 5
    }
  }, [])

  useEffect(() => {
    if (selectedCheckInDate) {
      const urlParams = new URLSearchParams(window.location.search)
      const hasBirthdays = urlParams.has('birthdays')

      if (
        hasBirthdays &&
        selectedCheckInDate.date === checkInDate.date &&
        checkInDate.status === 'OK' &&
        checkInDate.price &&
        selectedPlan?.planId === plan.planId
      ) {
        setShowModal(true)
        setSelectedCheckInDate(null)
      }

      // [#2925] 生年月日の入力後も「問い合わせ」のままなら、日本語版は続けて問い合わせフォームを開く（英語版はもう一度押すと WhatsApp 相談）
      if (
        hasValidBirthdays() &&
        selectedCheckInDate.date === checkInDate.date &&
        checkInDate.status === '-' &&
        selectedPlan?.planId === plan.planId
      ) {
        if (!isJpe) openLastMinuteInquiry()
        setSelectedCheckInDate(null)
      }
    }
  }, [selectedCheckInDate, checkInDate])

  const handleCloseModal = () => {
    setSelectedCheckInDate(null)
    setShowModal(false)
  }

  const displayPrice = (checkInDate: CheckInDateType) => {
    if (!checkInDate.price) {
      return null
    }

    if (!isJpe) {
      return formatJPYPrice(checkInDate.price)
    }

    return formatConvertedPrice(checkInDate.price, displayCurrency)
  }

  const openLastMinuteInquiry = () => {
    initializeLastMinuteInquiry({
      planId: plan.planId,
      startDate: checkInDate.date,
      endDate: checkInDate.endDate,
    })
    setShowLastMinuteInquiryModal(true)
  }

  const handleOpenModal = () => {
    if (checkInDate.status === '-') {
      // [#2925] 生年月日が未入力なら、予約できる日と同じく人数入力を求める。
      // 入力後の再表示で園の受入枠を判定し、満枠なら「満枠」に切り替わる
      if (!hasValidBirthdays()) {
        setSelectedCheckInDate(checkInDate)
        setSelectedPlan(plan)
        setOpenModalFilter(true)
        setValidationErrors({
          childBirthdate: true,
        })
        return
      }
      if (isJpe) {
        window.location.href = WHATSAPP_CONSULTATION_URL
        return
      }
      openLastMinuteInquiry()
      return
    }
    if (checkInDate.status !== 'OK') {
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const hasBirthdays = urlParams.has('birthdays')
    setSelectedCheckInDate(checkInDate)
    setSelectedPlan(plan)

    if (
      !hasBirthdays &&
      serviceTypes.some((serviceType) => serviceType.includes('保育園留学') || serviceType.includes('小学生留学'))
    ) {
      setOpenModalFilter(true)
      setValidationErrors({
        childBirthdate: true,
      })
      return
    }

    if (checkInDate.status === 'OK' && checkInDate.price) {
      setShowModal(true)
    }
  }

  if (Object.prototype.hasOwnProperty.call(checkInDate, 'id') && checkInDate.id === null) {
    return <></>
  }

  const dateLabel = (() => {
    const [y, m, d] = checkInDate.date.split('-').map(Number)
    const utcDate = new Date(Date.UTC(y, m - 1, d))
    const dayOfWeek = utcDate.getUTCDay()
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    return `${m}/${d}(${t(`day.formats.short.${dayKeys[dayOfWeek]}`)})`
  })()

  const renderLegacyStatus = () => (
    <>
      <div className={checkInDate.status === 'OK' ? 'text-green' : 'text-gray-400'}>
        {checkInDate.status === '-' && <IconInbox />}
        {checkInDate.status === 'OK' && (
          <svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
            <rect height="18" rx="9" stroke="#189855" strokeWidth="2" width="18" x="1" y="1" />
          </svg>
        )}
        {checkInDate.status === 'NG' && <span className="font-size-13">{t('reservation_confirm.modal_booking.sold_out')}</span>}
      </div>
      <div
        className={`text-xs w-full text-center tracking-wider ${
          checkInDate.status === 'OK' || checkInDate.status === '-' ? 'text-green' : 'text-gray-400'
        }`}
      >
        {checkInDate.status === '-' ? (
          <>{t('reservation_confirm.modal_booking.contact')}</>
        ) : (
          <>
            <div className="whitespace-nowrap overflow-hidden text-ellipsis w-full">{displayPrice(checkInDate)}</div>
            {isJpe && checkInDate.price && (
              <div className="whitespace-nowrap truncate text-xs">({formatJPYPrice(checkInDate.price)})</div>
            )}
          </>
        )}
      </div>
    </>
  )

  const renderEnglishStatus = () => {
    const statusColor = displayKind === 'sold_out' ? 'text-gray-600' : displayKind === 'unknown' ? 'text-gray-400' : 'text-green'
    const showPrice = displayKind === 'available' && checkInDate.price

    return (
      <>
        <div
          className={`flex flex-col items-center gap-0.5 ${statusColor}`}
          aria-label={availabilityStatusAriaLabel(displayKind, t)}
        >
          <AvailabilityStatusIcon kind={displayKind} size={18} />
          <AvailabilityStatusLabel
            kind={displayKind}
            variant={displayKind === 'contact' ? 'stacked' : 'inline'}
            className={statusColor}
          />
        </div>
        <div className="text-xs w-full text-center tracking-wider text-gray-900">
          {showPrice && (
            <>
              <div className="whitespace-nowrap overflow-hidden text-ellipsis w-full">{displayPrice(checkInDate)}</div>
              {checkInDate.price && (
                <div className="whitespace-nowrap truncate text-xs text-gray-900">({formatJPYPrice(checkInDate.price)})</div>
              )}
            </>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      {showModal && <DateInDateModal plan={plan} checkInDate={checkInDate} handleCloseModal={handleCloseModal} />}
      {showLastMinuteInquiryModal && <LastMinuteInquiryModal onClose={() => setShowLastMinuteInquiryModal(false)} />}
      <div
        ref={ref}
        style={{ width: '94px' }}
        className={`flex flex-col gap-2 items-center justify-between p-3 text-center transition-all duration-300 min-h-28 shrink-0 rounded-md ${
          isSelectable ? 'bg-white border-green border cursor-pointer' : 'bg-gray-100 border border-gray-300 cursor-not-allowed'
        }`}
        onClick={handleOpenModal}
      >
        <div className={`text-xs ${isSelectable ? 'text-green' : 'text-gray-600'}`}>{dateLabel}</div>
        {isJpe ? renderEnglishStatus() : renderLegacyStatus()}
      </div>
    </>
  )
}
